// Status light for an on-demand model endpoint, so a user understands *warming
// up* vs *ready* vs *parked* (scaled to zero, $0). Polls agentApi.modelServingStatus
// — for a platform-hosted model that is the SAME status Model Serving shows
// (lifecycle.endpoint_status); polls faster while warming so the cold start
// visibly resolves, then settles.
import { useEffect, useRef, useState } from "react";
import { agentApi } from "../../api/client";
import { COLORS } from "../../theme/colors";

const _DOT = { green: COLORS.green, amber: COLORS.orange, red: COLORS.pink, grey: COLORS.textDim };

// pure — the dot color for a light; unknown/missing → the neutral dim token.
export function lightColor(light) {
  return _DOT[light] || COLORS.textDim;
}

// pure — the warm-up step happening now, for a narrow cell: "Copying weights from S3
// · 64 / 121 GB · step 2 of 4". Same step labels as the full sequence.
export function currentStepText(steps) {
  const list = Array.isArray(steps) ? steps : [];
  const i = list.findIndex((s) => s.state === "active");
  if (i < 0) return null;
  return `${list[i].label} · step ${i + 1} of ${list.length}`;
}

// Pure presentational view of a serving status ({light,label,detail,steps}).
// Shared by ModelServingLight (fetches by endpoint) and AgentServingLight
// (fetches by agent) so the dot + label + warm-up sequence render identically.
// ``compact`` (table cells): only the step happening now, so the cell stays narrow.
export function ServingLightView({ status, compact = false }) {
  const warming = status.light === "amber" && Array.isArray(status.steps);
  const _stepColor = (s) => (s === "active" ? _DOT.amber : s === "done" ? _DOT.green : COLORS.textDim);
  const _stepMark = (s) => (s === "done" ? "✓ " : s === "active" ? "⋯ " : "○ ");
  return (
    <span data-testid="serving-light" title={status.detail || ""}
      style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 12.5, color: COLORS.text }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
        <span data-testid="serving-dot" data-light={status.light}
          style={{
            width: 9, height: 9, borderRadius: "50%", background: lightColor(status.light),
            boxShadow: status.light === "amber" ? `0 0 0 3px ${_DOT.amber}22` : "none",
          }} />
        <b>{status.label}</b>
      </span>
      {status.phase === "failed" && status.detail && (
        // why it failed + what to do — the same words Model Serving shows
        <span data-testid="serving-failure" style={{ fontSize: 11, color: COLORS.textMuted }}>{status.detail}</span>
      )}
      {warming && compact && currentStepText(status.steps) && (
        <span data-testid="serving-current-step" style={{ fontSize: 11, color: _DOT.amber, fontWeight: 600 }}>
          {currentStepText(status.steps)}
        </span>
      )}
      {warming && !compact && (
        // the ordered warm-up sequence — the user sees which step is happening now;
        // wraps rather than widening its container
        <span data-testid="serving-steps" style={{ display: "inline-flex", flexWrap: "wrap", columnGap: 10, rowGap: 2, fontSize: 11 }}>
          {status.steps.map((s) => (
            <span key={s.key} data-testid="serving-step" data-state={s.state}
              style={{ color: _stepColor(s.state), fontWeight: s.state === "active" ? 700 : 500 }}>
              {_stepMark(s.state)}{s.label}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

/** The live status of one on-demand endpoint (polled; faster while warming). */
export function useEndpointServingStatus(endpointId, { pollMs = 6000, warmingPollMs = 4000 } = {}) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await agentApi.modelServingStatus(endpointId);
        if (!alive) return;
        const st = res?.data || res;
        setStatus(st);
        setError(null);
        // reschedule: tighter cadence while warming so the cold start resolves visibly
        clearInterval(timer.current);
        timer.current = setInterval(load, st?.light === "amber" ? warmingPollMs : pollMs);
      } catch (e) {
        if (alive) setError(e?.message || String(e));
      }
    };
    load();
    return () => { alive = false; clearInterval(timer.current); };
  }, [endpointId, pollMs, warmingPollMs]);
  return { status, error };
}

export function ServingLightState({ status, error, compact = false }) {
  if (error) {
    return <span data-testid="serving-light-error" style={{ color: _DOT.red, fontSize: 12 }}>status unavailable</span>;
  }
  if (!status) {
    return <span data-testid="serving-light-loading" style={{ color: COLORS.textDim, fontSize: 12 }}>…</span>;
  }
  return <ServingLightView status={status} compact={compact} />;
}

export default function ModelServingLight({ endpointId, pollMs = 6000, warmingPollMs = 4000, compact = false }) {
  const { status, error } = useEndpointServingStatus(endpointId, { pollMs, warmingPollMs });
  return <ServingLightState status={status} error={error} compact={compact} />;
}
